import { useState } from "react";

import { Pagination } from "../../../../../components/design-system";
import { AD_TYPE_OPTIONS } from "../../constants/filters";
import { TABLE_ROW_HEIGHT_CLASS } from "../../constants/layout";
import { COPY } from "../../copy";
import { monthLabelFromIndex } from "../../customersData";
import { getCohortAdditionalOrders } from "../../monthDetailData";
import {
  applySegmentToActivityRows,
  formatOverviewCount,
  formatOverviewCurrency,
  getCurrentMonthIndex,
  getRowBudgetShare,
  getRowContinuedSales,
  type P8ActivityRow,
} from "../../p8OverviewData";
import { renderCopy } from "../../renderCopy";
import type { InsightsCustomerSegment } from "../../types";
import { useAdTypesModal } from "../AdTypesExplainer";
import { forecastTooltip } from "../ForecastExplainer";
import { InsightsFilterMenu } from "../InsightsFilterMenu";
import { InsightsSectionTitle } from "../InsightsSectionTitle";

import { DottedLabel } from "./DottedLabel";
import { HeaderTooltip } from "./HeaderTooltip";
import { ProjectedTag } from "./ProjectedTag";

type AdPerformanceByMonthTableProps = {
  /** Every month, uncut: the Ad type filter below the title belongs to this table. */
  rows: P8ActivityRow[];
  onOpenMonth: (monthIndex: number) => void;
};

/** Stands in for a figure that belongs in this column but isn't in yet. */
function EmptyCell() {
  return <span className="text-[#757575]">—</span>;
}

/**
 * A column the selected ad type can't report at all, which is a different thing
 * from a figure that hasn't landed. An em dash reads as "no value recorded", so
 * it would have a reader waiting for a number that is never coming; the hatch
 * reads as "switched off", which is what filtering to reorder ads does to
 * Continued ad sales.
 *
 * A native title rather than the dotted-underline tooltip the headers use: the
 * reason belongs to the column, not to each of eighteen identical cells, so this
 * is a quiet confirmation for anyone who hovers rather than a second place to
 * publish the explanation.
 */
function DisabledCell({ reason }: { reason: string }) {
  return (
    <span
      className="block h-4 w-full max-w-[72px] rounded"
      style={{
        backgroundImage:
          "repeating-linear-gradient(135deg, #e8e8e8 0 3px, #f7f7f7 3px 6px)",
      }}
      title={reason}
      aria-label={reason}
      role="img"
    />
  );
}

/**
 * A year and a half of months per page: long enough to cover a full year plus
 * the run-up to it for a year-over-year read, short enough that the card doesn't
 * bury the footnote below it.
 */
const PAGE_SIZE = 18;

const MONTH_LABEL_CLASS = "text-sm leading-5 font-medium";

/**
 * Month is frozen so the row stays identifiable while the reader scrolls the
 * thirteen columns to its right — a figure in the last column is meaningless if
 * you've lost which month it belongs to.
 *
 * The frozen cells need their own opaque fill. A row's background sits behind
 * every cell in it rather than on top of the ones scrolling past, so without a
 * fill of its own the sticky cell would have other columns' text sliding through
 * it. That's also why the hover fill is repeated here instead of being left to
 * the row, and why the row's bottom border is repeated on the cell: the table
 * collapses borders, which lets that fill paint over the collapsed line and
 * break the divider under this one column. Restating it collapses to the same
 * hairline, so it costs nothing where the browser was already drawing it.
 */
const STICKY_MONTH_CLASS = "sticky left-0 z-10";

/**
 * Only once there's something behind it. The table has no vertical rules, so a
 * permanent divider on this column would read as an arbitrary line rather than
 * as an edge — the shadow appears on scroll and says what it means.
 */
const STICKY_SCROLLED_CLASS = "shadow-[6px_0_6px_-4px_rgba(0,0,0,0.08)]";

/**
 * Pinned column widths, in render order and in pixels.
 *
 * Auto layout sizes each column to whatever happens to be in it, so changing the
 * customer filter reflows the whole table: figures become em dashes, digit
 * counts change, and every column shifts. Holding the widths still is the point
 * — the shape of the table shouldn't be what tells you the filter moved. Widths
 * are set from the headers, which are the widest thing in most columns and never
 * wrap.
 */
const COLUMNS: { key: string; width: number }[] = [
  { key: "month", width: 104 },
  { key: "budget", width: 88 },
  // Carries the "As of ..." detail line under the figure.
  { key: "adSpend", width: 124 },
  { key: "utilization", width: 104 },
  { key: "impressions", width: 104 },
  { key: "clicks", width: 88 },
  { key: "conversions", width: 104 },
  { key: "immediateAdSales", width: 152 },
  // Widest: the figure sits beside the lift tag.
  { key: "continuedAdSales", width: 156 },
  // Carries the dashed projected tag on months still short of 12.
  { key: "firstYearAdSales", width: 152 },
  { key: "immediateRoas", width: 120 },
  { key: "firstYearRoas", width: 128 },
  // Pairs with the zero-width row-action cell.
  { key: "explore", width: 0 },
];

const TABLE_MIN_WIDTH = COLUMNS.reduce(
  (total, column) => total + column.width,
  0
);

/** The segment-keyed tooltip copy is stored under short keys. */
const TOOLTIP_KEY: Record<
  InsightsCustomerSegment,
  "all" | "new" | "returning"
> = {
  "all-customers": "all",
  "new-customers": "new",
  "returning-customers": "returning",
};

const HEADER_TOOLTIPS = COPY.overview.monthTable;

/** Shared by Utilization and % of budget, which are both a share of budget. */
function formatBudgetPercent(value: number | null) {
  if (value == null) {
    return "—";
  }
  // One decimal, but drop a trailing ".0" (e.g. "88%" not "88.0%") to match the platform.
  const pct = Math.round(value * 1000) / 10;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`;
}

function BudgetPercentCell({ value }: { value: number | null }) {
  if (value == null) {
    return <EmptyCell />;
  }
  return (
    <span className="text-sm leading-5 text-[#333]">
      {formatBudgetPercent(value)}
    </span>
  );
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
    >
      <path
        d="M3 8H13M13 8L9 4M13 8L9 12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DeltaTag({ pct }: { pct: number }) {
  return (
    <span className="inline-flex items-center rounded bg-[#eef1ff] px-1.5 py-0.5 text-xs leading-4 font-medium text-[#3b3f8f]">
      +{pct}%
    </span>
  );
}

/**
 * Explains the lift tag, not the figure beside it — that's the cell's job. The
 * count and the sentence come from the same places the drilldown's tag reads
 * them from, so clicking the row can't produce a different number for the same
 * month.
 *
 * The tag goes bare when the cohort has no additional-order count to name: the
 * sentence is built around that count, and a percentage no reader can unpack is
 * a smaller cost than one claiming zero orders produced it.
 */
function ContinuedLiftTag({
  pct,
  monthIndex,
}: {
  pct: number;
  monthIndex: number;
}) {
  const additionalOrders = getCohortAdditionalOrders(monthIndex);

  if (additionalOrders == null || additionalOrders <= 0) {
    return <DeltaTag pct={pct} />;
  }

  return (
    <HeaderTooltip
      tooltip={renderCopy(
        HEADER_TOOLTIPS.liftTag,
        {
          count: formatOverviewCount(additionalOrders),
          orders: additionalOrders === 1 ? "order" : "orders",
          // Short form, matching the drilldown this row links to.
          month: monthLabelFromIndex(monthIndex),
          pct,
        },
        "font-semibold"
      )}
    >
      <DeltaTag pct={pct} />
    </HeaderTooltip>
  );
}

export function AdPerformanceByMonthTable({
  rows,
  onOpenMonth,
}: AdPerformanceByMonthTableProps) {
  const [page, setPage] = useState(1);
  // Scoped to this table rather than the tab. It only ever re-cut these rows,
  // and sitting up with the date range implied it also moved the headline
  // metrics and the charts, which it never did.
  const [segment, setSegment] =
    useState<InsightsCustomerSegment>("all-customers");
  // Mounted at the page root, since its other trigger is a link inside a
  // tooltip panel that unmounts under the pointer.
  const openAdTypesModal = useAdTypesModal();
  // Drives the frozen column's shadow. Re-setting it to the same value on every
  // scroll event is free — React bails out of the render when the state is
  // unchanged — so this needs no ref or throttle.
  const [isScrolled, setIsScrolled] = useState(false);
  const tooltipKey = TOOLTIP_KEY[segment];
  // Closed months read as links into the drilldown; the in-progress month is left
  // plain because its cohort is still forming. Keyed off the month index rather
  // than the "Active" detail line so it doesn't hang on a display string.
  const currentMonthIndex = getCurrentMonthIndex();
  const segmentRows = applySegmentToActivityRows(rows, segment);
  const totalPages = Math.max(1, Math.ceil(segmentRows.length / PAGE_SIZE));
  // Clamped rather than reset: the segment filter re-cuts the same months, so
  // changing it should leave you on the page you were reading.
  const currentPage = Math.min(page, totalPages);
  const pageRows = segmentRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  // Utilization forecasts the whole month's budget against all ad types, so it
  // can't be cut to one. Filtered, the column reports that ad type's spend as a
  // share of the shared budget instead — a different measure, hence a different
  // header. Reorders land in the month they happen, so a reorder-ad view has no
  // continued sales and no first year to project a total or a return into.
  const showsUtilization = segment === "all-customers";
  // Continued ad sales is the one column reorder ads genuinely can't report:
  // nothing continues past the click month. The two first-year columns can —
  // their figures just equal the immediate ones — so they report on every
  // filter. Either way the columns stay in place and the cells that can't report
  // are disabled, rather than the table changing shape underneath the reader.
  const continuedApplies = segment !== "returning-customers";
  const stickyMonthClass = isScrolled
    ? `${STICKY_MONTH_CLASS} ${STICKY_SCROLLED_CLASS}`
    : STICKY_MONTH_CLASS;

  return (
    <section className="bg-fs-surface-primary rounded-lg border border-[#dfe0e1] p-6">
      {/* The title tooltip carries the click-month rule and the refresh
          cadence — everything about this table a reader can't get from the
          column headers. */}
      <h3>
        <InsightsSectionTitle tooltip={HEADER_TOOLTIPS.titleTooltip}>
          {HEADER_TOOLTIPS.title}
        </InsightsSectionTitle>
      </h3>
      {/* Below the title rather than beside it, so it reads as scoping the rows
          under it rather than the card as a whole. */}
      <div className="mt-3 flex">
        <InsightsFilterMenu
          name="month-table-ad-type"
          labelPrefix="Ad type"
          menuTitle="Filter by ad type"
          value={segment}
          options={AD_TYPE_OPTIONS}
          onChange={setSegment}
          clearValue="all-customers"
          titleInfo={
            openAdTypesModal
              ? {
                  label: COPY.overview.adTypesModal.title,
                  onClick: openAdTypesModal,
                }
              : undefined
          }
        />
      </div>
      {/* `isolate` keeps the frozen column's z-10 inside this container. Slate
          portals the Ad type popup to the body with no z-index of its own, and a
          positioned element with a raised z-index paints above one at `auto`
          whatever the DOM order — so without this the Month column covers the
          menu above it. Contained here, the frozen column still outranks the
          cells scrolling under it and nothing else. */}
      <div
        className="isolate mt-4 overflow-x-auto"
        onScroll={(event) => setIsScrolled(event.currentTarget.scrollLeft > 0)}
      >
        {/* 8px on each end of every row, header included, so the first and last
            columns aren't flush against the card. Applied by position rather
            than to specific cells because the trailing cell is the zero-width
            row action. */}
        <table
          className="w-full table-fixed text-left text-sm [&_tr>*:first-child]:pl-2 [&_tr>*:last-child]:pr-2"
          style={{ minWidth: `${TABLE_MIN_WIDTH}px` }}
        >
          <colgroup>
            {COLUMNS.map((column) => (
              <col key={column.key} style={{ width: `${column.width}px` }} />
            ))}
          </colgroup>
          <thead>
            <tr className="bg-fs-surface-primary border-b border-[#dfe0e1]">
              <th
                className={`faire-label-medium ${stickyMonthClass} bg-fs-surface-primary border-b border-[#dfe0e1] py-3 pr-3 whitespace-nowrap text-[#333]`}
              >
                Month
              </th>
              <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                Budget
              </th>
              <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                <HeaderTooltip tooltip={HEADER_TOOLTIPS.adSpend}>
                  <DottedLabel>Ad spend</DottedLabel>
                </HeaderTooltip>
              </th>
              <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                {/* Picked off `segment` rather than the shared `tooltipKey`,
                    which carries an `all` case this column can't have: under all
                    ad types the header is Utilization instead. */}
                <HeaderTooltip
                  tooltip={
                    showsUtilization
                      ? HEADER_TOOLTIPS.utilization
                      : HEADER_TOOLTIPS.budgetShare[
                          segment === "new-customers" ? "new" : "returning"
                        ]
                  }
                >
                  <DottedLabel>
                    {showsUtilization ? "Utilization" : "% of budget"}
                  </DottedLabel>
                </HeaderTooltip>
              </th>
              <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                Impressions
              </th>
              <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                Clicks
              </th>
              <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                <HeaderTooltip tooltip={HEADER_TOOLTIPS.conversions}>
                  <DottedLabel>Conversions</DottedLabel>
                </HeaderTooltip>
              </th>
              <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                <HeaderTooltip tooltip={HEADER_TOOLTIPS.immediateAdSales}>
                  <DottedLabel>Immediate ad sales</DottedLabel>
                </HeaderTooltip>
              </th>
              <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                <HeaderTooltip
                  tooltip={COPY.overview.continuedAdSales[tooltipKey]}
                >
                  <DottedLabel>Continued ad sales</DottedLabel>
                </HeaderTooltip>
              </th>
              <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                {/* The link only rides the forecasting branch: a reorder ad's
                    first year is already complete, so there is no forecast to
                    explain. */}
                <HeaderTooltip
                  interactive={continuedApplies}
                  tooltip={
                    continuedApplies
                      ? forecastTooltip(HEADER_TOOLTIPS.firstYearAdSales)
                      : HEADER_TOOLTIPS.firstYearAdSalesReorder
                  }
                >
                  <DottedLabel>First-year ad sales</DottedLabel>
                </HeaderTooltip>
              </th>
              <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                <HeaderTooltip tooltip={HEADER_TOOLTIPS.firstOrderRoas}>
                  <DottedLabel>Immediate ROAS</DottedLabel>
                </HeaderTooltip>
              </th>
              <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                <HeaderTooltip
                  interactive={continuedApplies}
                  tooltip={
                    continuedApplies
                      ? forecastTooltip(HEADER_TOOLTIPS.firstYearRoas)
                      : HEADER_TOOLTIPS.firstYearRoasReorder
                  }
                >
                  <DottedLabel>First-year ROAS</DottedLabel>
                </HeaderTooltip>
              </th>
              {/* Pairs with the row-action anchor cell below; collapses to nothing. */}
              <th className="w-0 p-0" aria-label="Explore" />
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => (
              <tr
                key={row.key}
                className={`group cursor-pointer border-b border-[#dfe0e1] last:border-0 hover:bg-[#fafafa] ${TABLE_ROW_HEIGHT_CLASS}`}
                onClick={() => onOpenMonth(row.monthIndex)}
              >
                <td
                  className={`${stickyMonthClass} bg-fs-surface-primary border-b border-[#dfe0e1] py-3 pr-3 whitespace-nowrap text-[#333] group-last:border-0 group-hover:bg-[#fafafa]`}
                >
                  <div
                    className={
                      row.monthIndex === currentMonthIndex
                        ? MONTH_LABEL_CLASS
                        : `${MONTH_LABEL_CLASS} underline`
                    }
                  >
                    {row.periodLabel}
                  </div>
                  {row.periodDetail ? (
                    <div className="type-fs-label-medium text-[#49694c]">
                      {row.periodDetail}
                    </div>
                  ) : null}
                </td>
                <td className="px-3 py-3 text-[#333]">
                  <div className="text-sm leading-5 underline">
                    {row.budget}
                  </div>
                </td>
                <td className="px-3 py-3 text-[#333]">
                  <div className="text-sm leading-5">{row.adSpend}</div>
                  {row.adSpendDetail ? (
                    <div className="text-xs leading-4 whitespace-nowrap text-[#757575]">
                      {row.adSpendDetail}
                    </div>
                  ) : null}
                </td>
                <td className="px-3 py-3">
                  {/* Only utilization gets the projected tag. A share of budget
                      is spend that has already gone out, so it's a real figure
                      even in a month that's still running. */}
                  {!showsUtilization ? (
                    <BudgetPercentCell value={getRowBudgetShare(row)} />
                  ) : row.utilization == null ? (
                    <EmptyCell />
                  ) : row.utilizationAsTag ? (
                    <ProjectedTag>
                      {formatBudgetPercent(row.utilization)}
                    </ProjectedTag>
                  ) : (
                    <BudgetPercentCell value={row.utilization} />
                  )}
                </td>
                <td className="px-3 py-3 text-sm leading-5 text-[#333]">
                  {row.impressions}
                </td>
                <td className="px-3 py-3 text-sm leading-5 text-[#333]">
                  {row.clicks}
                </td>
                <td className="px-3 py-3 text-sm leading-5 text-[#333] underline">
                  {row.conversions}
                </td>
                <td className="px-3 py-3 text-sm leading-5 text-[#333]">
                  {row.firstOrderSales}
                </td>
                <td className="px-3 py-3 text-sm leading-5 text-[#333]">
                  {continuedApplies ? (
                    <span className="inline-flex items-center gap-2">
                      {formatOverviewCurrency(getRowContinuedSales(row))}
                      {row.salesToDateDeltaPct != null &&
                      row.salesToDateDeltaPct > 0 ? (
                        <ContinuedLiftTag
                          pct={row.salesToDateDeltaPct}
                          monthIndex={row.monthIndex}
                        />
                      ) : null}
                    </span>
                  ) : (
                    <DisabledCell reason={HEADER_TOOLTIPS.disabledContinued} />
                  )}
                </td>
                {/* Reported on every filter. Under reorder ads the figure equals
                    Immediate ad sales and is final, so the data layer also drops
                    the projected tag rather than dashing a settled number. */}
                <td className="px-3 py-3">
                  {row.firstYearRoasProjected ? (
                    <ProjectedTag>
                      {formatOverviewCurrency(row.firstYearSalesAmount)}
                    </ProjectedTag>
                  ) : (
                    <span className="text-sm leading-5 text-[#333]">
                      {formatOverviewCurrency(row.firstYearSalesAmount)}
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 text-sm leading-5 text-[#333]">
                  {row.firstOrderRoas}
                </td>
                <td className="px-3 py-3">
                  {row.firstYearRoasProjected ? (
                    <ProjectedTag>{row.firstYearRoas}</ProjectedTag>
                  ) : (
                    <span className="text-sm leading-5 text-[#333]">
                      {row.firstYearRoas}
                    </span>
                  )}
                </td>
                {/* Zero-width anchor: the action reserves no column, so the data
                    spans the full width and the link slides in over the row's
                    right edge on hover. `overflow-hidden` clips the resting
                    offset so it can't widen the table's scroll area. */}
                <td className="relative w-0 p-0">
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center overflow-hidden bg-gradient-to-l from-[#fafafa] via-[#fafafa] to-transparent pl-12 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                    <span className="flex translate-x-3 items-center gap-1.5 text-sm leading-5 whitespace-nowrap text-[#333] transition-transform duration-150 group-hover:translate-x-0">
                      <span className="underline">Explore</span>
                      <ArrowRightIcon />
                    </span>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 ? (
        <div className="mt-4 flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onClick={setPage}
          />
        </div>
      ) : null}
    </section>
  );
}
